import { QueryClient, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { AttachmentItemDto, EmailSendModeDto, EmailSummaryDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import {
  AppAttachmentWithoutContent,
  DeltaEmailBasicUpdate,
  EmailBodyContentType,
} from '@/models/BasicEmail.model.ts';
import { refreshThreadList } from '@/views/emails/util/threadListCache.util.ts';

const COALESCE_WINDOW_MS = 500;

type PendingRefresh = {
  folders: boolean;
  threadIds: Set<string>;
  threads: boolean;
};

let pending: PendingRefresh | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flush = (queryClient: QueryClient): void => {
  const batch = pending;
  pending = null;
  flushTimer = null;
  if (!batch) {
    return;
  }

  if (batch.folders) {
    void queryClient.refetchQueries({ queryKey: [QueryKeyEnum.EMAILS.FOLDERS_STATE] });
  }
  if (batch.threads) {
    void refreshThreadList(queryClient);
    void queryClient.invalidateQueries({
      queryKey: [QueryKeyEnum.DASHBOARD.EMAILS],
      refetchType: 'active',
    });
  }
  batch.threadIds.forEach(threadId => {
    void queryClient.refetchQueries(
      { queryKey: [QueryKeyEnum.EMAILS.THREAD, threadId] },
      { throwOnError: false },
    );
  });
};

const scheduleRefresh = (
  queryClient: QueryClient,
  part: { folders?: boolean; threadId?: string; threads?: boolean },
): void => {
  pending ??= { folders: false, threadIds: new Set<string>(), threads: false };
  pending.folders ||= part.folders ?? false;
  pending.threads ||= part.threads ?? false;
  if (part.threadId) {
    pending.threadIds.add(part.threadId);
  }
  flushTimer ??= setTimeout(() => flush(queryClient), COALESCE_WINDOW_MS);
};

type Req = {
  currentFolderId?: string;
  currentPage: number;
  newEmail: DeltaEmailBasicUpdate;
};

type Return = {
  addNewEmail: (req: Req) => Promise<void>;
};

export const useAddNewEmail = (): Return => {
  const queryClient = useQueryClient();

  return {
    // eslint-disable-next-line @typescript-eslint/require-await
    addNewEmail: async req => {
      scheduleRefresh(queryClient, { folders: true });
      const { currentFolderId, newEmail } = req;
      if (newEmail.email.userFolderId !== currentFolderId) {
        console.debug('newEmail.userFolderId !== currentFolderId');
        return;
      }
      const email = mapDeltaToEmail(newEmail.email);
      if (!email) {
        console.debug('!email');
        return;
      }

      scheduleRefresh(queryClient, {
        threadId: newEmail.email.emailThreadId.value?.value,
        threads: true,
      });
    },
  };
};

const mapDeltaToEmail = (newEmail: DeltaEmailBasicUpdate['email']): EmailSummaryDto | null => {
  if (
    !newEmail.emailThreadId.value?.value ||
    (!newEmail.from.value?.value && !newEmail.isDraft.value)
  ) {
    return null;
  }
  return {
    attachments: (newEmail.attachments.value ?? []).map(mapAttachments),
    attachmentsCount: newEmail.attachmentsCount.value ?? 0,
    bcc: newEmail.cc.value?.map(it => it.value) ?? [],
    body: newEmail.body.value ?? '',
    bodyContentType: newEmail.bodyContentType.value ?? EmailBodyContentType.TEXT,
    bodyPreview: newEmail.bodyPreview.value ?? '',
    cc: newEmail.cc.value?.map(it => it.value) ?? [],
    date: newEmail.sentDateTime.value ?? dayjs().set('year', 1970).toString(),
    emailId: newEmail.externalEmailId.value,
    from: newEmail.from.value?.value ?? '',
    isDraft: newEmail.isDraft.value ?? false,
    isRead: newEmail.isRead.value ?? true,
    sendMode: newEmail.sendMode.value ?? EmailSendModeDto.New,
    subject: newEmail.subject.value ?? '',
    threadId: newEmail.emailThreadId.value?.value,
    to: newEmail.to.value?.map(it => it.value) ?? [],
  };
};

const mapAttachments = (attachment: AppAttachmentWithoutContent): AttachmentItemDto => {
  return {
    attachmentId: attachment.attachmentId,
    contentType: attachment.contentType,
    externalAttachmentId: attachment.externalAttachmentId.value,
    name: attachment.fileName,
  };
};
